'use client';

import React from 'react';

import {useI18n} from '@udemy-v2/i18n';
import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {useTracker} from '@udemy-v2/event-tracking';
import {Icon} from '@udemy-v2/icons';
import {Button, Image} from '@udemy-v2/react-core-components';
import {
    Carousel,
    CarouselSlide,
    useCarouselSlideLayout4Cards,
} from '@udemy-v2/react-carousel-components';
import {BlockQuoteCard} from '@udemy-v2/react-product-card-components';

import {Heading} from '@/lib/heading-level';
import {stripHtml} from '@/lib/strip-html';
import {
    DynamicWebContentPageType,
    useDynamicWebContentTestimonialsQuery,
} from '@/generated/graphql';

import {useCmsPreviewContent} from '../../data/hooks/use-cms-preview-content';
import {adaptTestimonialsData} from '../../data/parse';
import type {TestimonialsData} from '../../data/types';
import {
    createTestimonialClickEvent,
    getTestimonialEventType,
    TESTIMONIAL_CASE_STUDY_LABEL,
} from '../../data/tracking';
import styles from './testimonials.module.scss';
import {TestimonialCardCta} from './testimonial-card-cta.client';
import {TestimonialCardImpression} from './testimonial-card-impression.client';

const STORIES_URL = 'https://about.udemy.com/stories/';

function normalizeTestimonials(data: TestimonialsData): {
    heading: string;
    cards: TestimonialsData['contents'];
    viewAllUrl: string;
} {
    return {
        heading: data.heading,
        cards: data.contents,
        viewAllUrl: STORIES_URL,
    };
}

interface TestimonialsContentProps {
    initialData: TestimonialsData;
    headingId: string;
}

export const TestimonialsContent: React.FC<TestimonialsContentProps> = ({
    initialData,
    headingId,
}) => {
    const data = useCmsPreviewContent(initialData, {
        fetcher: useDynamicWebContentTestimonialsQuery.fetcher,
        variables: {pageType: DynamicWebContentPageType.LoggedOutHomePage},
        adapt: adaptTestimonialsData,
    });

    const {heading, cards, viewAllUrl} = normalizeTestimonials(data);

    const {gettext} = useI18n();
    const tracker = useTracker();
    const isMobileMax = useMatchMedia('xs-max');
    const slideLayout = useCarouselSlideLayout4Cards();

    const slides = cards.map((group) => {
        const person = group.contents[0];
        const slideLabel = stripHtml(person?.heading || group.description);
        const eventType = getTestimonialEventType(group.labelText);
        const isCaseStudy = group.labelText === TESTIMONIAL_CASE_STUDY_LABEL;

        return (
            <CarouselSlide
                key={group.trackingId || person?.heading}
                aria-label={slideLabel}
                className={styles.slide}
            >
                <TestimonialCardImpression
                    id={group.trackingId}
                    type={eventType}
                    url={group.contentUrl}
                    isCaseStudy={isCaseStudy}
                >
                    <BlockQuoteCard
                        className={styles.card}
                        attribution={
                            <span dangerouslySetInnerHTML={{__html: person?.heading ?? ''}} />
                        }
                        attributionDescription={
                            person ? (
                                <span dangerouslySetInnerHTML={{__html: person.description}} />
                            ) : undefined
                        }
                        attributionImage={
                            person ? (
                                <Image
                                    className={styles.avatar}
                                    src={person.image.url}
                                    alt=""
                                    width={46}
                                    height={46}
                                />
                            ) : undefined
                        }
                        footer={
                            <TestimonialCardCta
                                id={group.trackingId}
                                type={eventType}
                                url={group.contentUrl}
                                text={group.contentUrlText}
                                isCaseStudy={isCaseStudy}
                            />
                        }
                    >
                        <span dangerouslySetInnerHTML={{__html: group.description}} />
                    </BlockQuoteCard>
                </TestimonialCardImpression>
            </CarouselSlide>
        );
    });

    return (
        <>
            <Heading
                id={headingId}
                className={`${styles.heading} ud-heading-xl`}
                dangerouslySetInnerHTML={{__html: heading}}
            />
            <Carousel
                ariaLabel={stripHtml(heading)}
                slideLayout={slideLayout}
                controlLayout="pagination-inline"
                slides={slides}
            />
            {!isMobileMax && (
                <Button
                    componentClass="a"
                    href={viewAllUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    udStyle="ghost"
                    className={styles['view-all-link']}
                    onClick={() =>
                        tracker.publishEvent(
                            createTestimonialClickEvent({
                                id: 'lohp_testimonials',
                                type: '',
                                url: viewAllUrl,
                                uiRegion: 'body',
                            }),
                        )
                    }
                >
                    {gettext('View all stories')}
                    <Icon glyph="next" label={false} className="ud-icon-bi-directional" />
                </Button>
            )}
        </>
    );
};
